
import React, { useEffect, useRef, useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react'

interface FilteredDropdownProps {
  options: FilteredDropdownOption[];
  disabled?: boolean;
  value: string | number;
  label?: string;
  onChange: (newValue: string | number) => void;
}

interface FilteredDropdownOption {
  value: string | number;
  displayText: string;
  subText?: string;
}

export default function FilteredDropdown({
  label,
  disabled,
  onChange,
  options,
  value,
}: FilteredDropdownProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setQuery('');
    if (value && options) {
      const selectedOption = options.find(opt => opt.value === value);
      if (selectedOption && !filteredOptions.includes(selectedOption)) {
        filteredOptions.unshift(selectedOption);
      }
    }
  }, [value, options])

  const filteredOptions =
    query === ''
      ? options
      : options.filter(opt => {
          const optionText = `${opt.displayText}${opt.subText ?? ''}`.toLowerCase();
          return optionText.includes(query.toLowerCase()) || opt.value === value;
        });

  const handleInputFocus = () => {
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  return (
    <Combobox
      as="div"
      value={value}
      onChange={(newValue) => {
        onChange(newValue);
        setQuery('');
      }}
      disabled={disabled}
    >
      <Label className="block text-sm font-medium leading-6 text-gray-900">{label ?? ''}</Label>
      <div className="relative mt-2">
        <ComboboxButton as='div'>
          <ComboboxInput
            ref={inputRef}
            aria-disabled={disabled}
            className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(val: string | number) => {
              const findByValue = filteredOptions?.find(op => op.value === val);
              return findByValue?.displayText
            }}
            onFocus={handleInputFocus}
          />
        </ComboboxButton>
        <ComboboxButton
          className={`absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none`}
        >
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>
        {filteredOptions?.length > 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOptions.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option.value}
                className={({ active }) => `relative cursor-default select-none py-2 pl-3 pr-9 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'}`}
              >
                {({ active, selected }) => (
                  <>
                    <div className="flex">
                      <span className={`truncate ${selected ? 'font-semibold' : ''}`}>{option.displayText}</span>
                      <span
                        className={`ml-2 truncate capitalize text-gray-500 ${active ? 'text-blue-200' : 'text-gray-500'}`}
                      >
                        {option?.subText ?? ''}
                      </span>
                    </div>

                    {selected && (
                      <span
                        className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-blue-600'}`}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  )
}
